import React from 'react';
import { MECH, StatRow, renderArrows, EdgeGlyph, HpChip, DMG_OWNER_COLOR } from './glyphs';
import { TAG_META, StatusTagPill, statusAsideLines } from '../CombatPlayerbar/statusTags';
import { STATUS_TAG_ICONS } from '../CombatPlayerbar/statusWordGlyphs';
import { ENEMY_STATUS_META, resolveEnemyStatus } from '../CombatStatusRails/enemyStatusMeta';
import EnemyStatusPill from '../CombatStatusRails/EnemyStatusPill';
import EnemyStatusChip from '../CombatStatusRails/EnemyStatusChip';
import { DamageSword, SequelaeGlyph } from '../CombatPlayerbar/ResultPillIcons';
import './GameTip.css';

/* Decode lines — the canonical hand-authored enemy grammar (tooltip canon,
   2026-06-11). An enemy's mechanic is written ONCE as token lines and rendered
   on every surface that explains it: big on the pre-combat announce card,
   small inline in the enemy-strip tooltip.

   A line is an array of tokens:
     { tag: 'lipid', v?: '1' }   → the TRUE HUD status tag for that word.
                                   announce mode: the real bls pill + hover card
                                   tip mode: inline glyph+word anchor (a tooltip
                                   must never nest another tooltip)
     { g: 'hp', v: '−5 max' }    → canonical mechanic glyph + value (MECH)
     { j: '→' }                  → muted connective ("→", "·", "when", "per round")
     { t: 'permanent' }          → plain fragment

   Authored entries live in announceDecode.js. Spec:
   wiki/decisions/2026-06-11-tooltip-canon.md */

/* ⭐ WHERE THIS ENEMY CARD IS BEING RENDERED — the one explicit option that decides
   whether the status chips embedded in its body are live or decorative.

   An enemy card body is composed ONCE (enemyTooltipSections) and mounted in two
   structurally different places, and the difference is not cosmetic:

     'nested-hover'    (DEFAULT) — the body is inside a hover popover: the enemy rail
                       card, the solo HP-bar tip, the warden mini inside a drug
                       tooltip. A tooltip must never nest another tooltip, so every
                       embedded chip is decorative chrome and its meaning is surfaced
                       INLINE beside it instead.
     'standalone-card' — the body IS the page panel, not a popover: the outright
                       Engage / ANNOUNCE card. There is no host hover to nest inside,
                       so a chip may carry its own canonical hover/focus tooltip, the
                       same one the live HUD tag has.

   Named rather than boolean because it states the FACT about the mount that decides
   the behaviour, not the behaviour itself. It travels as React context so every chip
   the body composes reads it from one place: threading a prop through
   InflictsRow / DealsRow / AuraRow / renderDecodeToken / ConvertChip / FormulaToken is
   six chances for one of them to be missed, which shows up as a single dead chip.
   ⛔ The default is the nested reading, so nothing but an explicit opt-in changes. */
export const ENEMY_CARD_RENDER_CONTEXT = {
  NESTED_HOVER: 'nested-hover',
  STANDALONE_CARD: 'standalone-card',
};

const EnemyCardRenderContext = React.createContext(ENEMY_CARD_RENDER_CONTEXT.NESTED_HOVER);

/* True only where a nested tooltip is structurally legal. */
export function useEmbeddedChipsInteractive() {
  return React.useContext(EnemyCardRenderContext) === ENEMY_CARD_RENDER_CONTEXT.STANDALONE_CARD;
}

/* ⭐ AN OPTIONAL EXTRA STATUS VOCABULARY, for a surface that draws enemies the game does
   not field yet (the Compendium's design-only bosses): `{ player: { key: TAG_META-shaped
   entry }, enemy: { key: ENEMY_STATUS_META-shaped entry } }`. It can only ADD keys. The
   game's registries win every collision, and with no vocabulary every lookup below is the
   registry alone, so no card the game fields can change. It arrives as
   `opts.statusVocabulary` on enemyTooltipSections and reaches the row components by
   context, the same way the render context does. */
const StatusVocabularyContext = React.createContext(null);

function playerStatusMeta(tag, vocab) {
  return TAG_META[tag] || vocab?.player?.[tag] || null;
}

function isEnemyStatus(tag, vocab) {
  return Boolean(ENEMY_STATUS_META[tag] || vocab?.enemy?.[tag]);
}

function resolveVocabEnemyStatus(tag, vocab) {
  return resolveEnemyStatus({ key: tag }, ENEMY_STATUS_META[tag] || vocab?.enemy?.[tag]);
}

/* THE embedded player-status chip. One component so "is this chip live here?" is
   answered in exactly one place; interactive it is the canonical StatusTagPill hover
   (bare panel, the status' own def), static it is the same pill with no host at all. */
function EmbeddedStatusChip({ word, meta }) {
  return <StatusTagPill word={word} meta={meta} interactive={useEmbeddedChipsInteractive()} />;
}

/* THE embedded enemy-status chip — the enemy-side mirror of the above. Interactive it
   is the canonical EnemyStatusChip (the same hover card the status rail renders),
   hosted on a <span> because every element in a tooltip body is inline. */
function EmbeddedEnemyStatusChip({ resolved }) {
  if (!useEmbeddedChipsInteractive()) return <EnemyStatusPill resolved={resolved} labelOnly />;
  return <EnemyStatusChip as="span" resolved={resolved} labelOnly />;
}

function TagToken({ token, mode }) {
  const meta = TAG_META[token.tag];
  if (!meta) {
    // Enemy-side status key (glucose, inr, division…): render the enemy chip
    // instead of the player pill. Gated on explicit ENEMY_STATUS_META membership
    // so a stray non-status word never upgrades to a chip.
    if (ENEMY_STATUS_META[token.tag]) {
      const r = resolveEnemyStatus({ key: token.tag });
      if (mode === 'announce') return <EnemyStatusPill resolved={r} labelOnly />;
      const EIcon = r.Icon;
      return (
        <span className="status-word" style={{ color: r.color }}>
          {EIcon && <EIcon size={13} />}
          {r.label}
        </span>
      );
    }
    return <span className="gt-text">{token.tag}</span>;
  }
  if (mode === 'announce') {
    // Valued tags (e.g. pressure) keep their count-before-pill layout. A
    // MULTIPLIER on a tag is a different fact and takes the `.gt-formula` route
    // instead — see resolveStackDeltaTokens.
    return (
      <>
        {token.v !== undefined && <span className="gt-decode-count">{token.v}</span>}
        <StatusTagPill word={token.tag} meta={meta} />
      </>
    );
  }
  // tip mode: flat glyph+word anchor in the tag's color (no nested tooltip).
  const icon = STATUS_TAG_ICONS[meta.iconKey];
  return (
    <span className="status-word" style={{ color: meta.color }}>
      {token.v !== undefined && <span className="gt-decode-count">{token.v} </span>}
      {icon && icon(13)}
      {token.tag}
    </span>
  );
}

export function DecodeLines({ lines, mode = 'tip', className = '' }) {
  const valid = (lines || []).filter((l) => Array.isArray(l) && l.length);
  if (!valid.length) return null;
  return (
    <span
      className={[
        'gt-decode',
        mode === 'announce' ? 'gt-decode--announce' : 'gt-decode--tip',
        className,
      ].filter(Boolean).join(' ')}
    >
      {valid.map((tokens, li) => (
        <span key={li} className="gt-statrow gt-decode-line">
          {tokens.filter(Boolean).map((tk, i) => {
            if (tk.tag) return <TagToken key={i} token={tk} mode={mode} />;
            if (tk.edge !== undefined) return <EdgeGlyph key={i} label={tk.edge} />;
            if (tk.j) return <span key={i} className="gt-join">{renderArrows(tk.j)}</span>;
            if (tk.t !== undefined) return <span key={i} className="gt-text">{renderArrows(tk.t)}</span>;
            if (tk.g === 'hp' && tk.chip) return <HpChip key={i} value={tk.v} color={tk.color} />;
            const mech = MECH[tk.g];
            const Icon = mech?.Icon;
            const color = tk.accent ? 'var(--effect-color, #fbbf24)' : (tk.color || mech?.color);
            return (
              <span key={i} className="gt-stat" style={color ? { color } : undefined}>
                {Icon && <Icon size={13} className="gt-stat__icon" />}
                {tk.v !== undefined && <span className="gt-stat__v">{tk.v}</span>}
              </span>
            );
          })}
        </span>
      ))}
    </span>
  );
}

function tokenToStat(tk, vocab = null) {
  if (tk.tag) {
    const meta = playerStatusMeta(tk.tag, vocab);
    if (!meta && isEnemyStatus(tk.tag, vocab)) {
      const r = resolveVocabEnemyStatus(tk.tag, vocab);
      const EIcon = r.Icon;
      return {
        icon: EIcon ? <EIcon size={13} className="gt-stat__icon" /> : undefined,
        v: r.label,
        color: r.color,
      };
    }
    const icon = meta && (meta.icon || STATUS_TAG_ICONS[meta.iconKey]);
    return {
      icon: icon ? icon(13, 'gt-stat__icon') : undefined,
      v: tk.v !== undefined ? `${tk.v} ${tk.tag}` : tk.tag,
      color: meta?.color,
    };
  }
  return tk;
}

export function DispenseChip({ detail = '' }) {
  return (
    <span className="gt-result-chip gt-dispense-chip">
      <span className="gt-result-chip__label gt-dispense-chip__label">Dispense</span>
      {detail ? <span className="gt-result-chip__detail">{detail}</span> : null}
    </span>
  );
}

/* Render the full authored decode-token grammar shared by structured enemy
   definitions and persistent aura content. */
function renderDecodeToken(tk, i, vocab = null) {
  // Enemy-side status word → the embedded enemy chip (labelOnly), same chrome as
  // the InflictsRow enemy case. Live or decorative per the card's render context.
  if (tk.tag && isEnemyStatus(tk.tag, vocab)) {
    return <EmbeddedEnemyStatusChip key={i} resolved={resolveVocabEnemyStatus(tk.tag, vocab)} />;
  }
  // Player HUD status tag → the embedded pill.
  if (tk.tag && playerStatusMeta(tk.tag, vocab)) {
    return <EmbeddedStatusChip key={i} word={tk.tag} meta={playerStatusMeta(tk.tag, vocab)} />;
  }
  // Unknown tag → visible text, never dropped.
  if (tk.tag) return <span key={i} className="gt-text">{renderArrows(tk.tag)}</span>;
  if (tk.dispense) return <DispenseChip key={i} />;
  // Inline HP number → the reusable status-tag HP chip.
  if (tk.g === 'hp' && tk.chip) return <HpChip key={i} value={tk.v} color={tk.color} />;
  // Labeled causal edge (edge('Hardens') → "—Hardens→").
  if (tk.edge !== undefined) return <EdgeGlyph key={i} label={tk.edge} />;
  // Damage-formula tokens (numeral / operator / clinical label).
  if (tk.n !== undefined) return <span key={i} className="gt-dmg-num">{tk.n}</span>;
  if (tk.op !== undefined) return <span key={i} className="gt-op">{tk.op}</span>;
  if (tk.clin !== undefined) return <span key={i} className="gt-clin">{tk.clin}</span>;
  if (tk.j) return <span key={i} className="gt-join">{renderArrows(tk.j)}</span>;
  if (tk.t !== undefined) return <span key={i} className={tk.italic ? 'gt-text gt-text--italic' : 'gt-text'}>{renderArrows(tk.t)}</span>;
  // Glyph-stat pair — full color grammar (accent → host color, damage →
  // owner-colored sword, else the mechanic's canon color).
  const mech = MECH[tk.g];
  const Icon = mech?.Icon;
  const ownerColor = tk.g === 'damage' && tk.owner ? DMG_OWNER_COLOR[tk.owner] : undefined;
  const color = tk.accent ? 'var(--effect-color, #fbbf24)' : (tk.color || ownerColor || mech?.color);
  return (
    <span key={i} className="gt-stat" style={color ? { color } : undefined}>
      {Icon && <Icon size={13} className="gt-stat__icon" />}
      {tk.v !== undefined && <span className="gt-stat__v">{tk.v}</span>}
    </span>
  );
}

/* Group a contiguous run of math tokens (numeral / operator / dispense chip /
   clinical label) into ONE .gt-formula inline-flex — the SAME canonical uniform-
   gap readout the mini Deals rows use — so a cluster like "2 × [Dispense]" spaces
   evenly: gap(2,×) == gap(×,chip), instead of riding the looser row gap where the
   mono numeral's side-bearing made 2↔× read wider than ×↔chip (Edward 2026-07-14:
   "the margin between the 2 and the x [must] match the margin between the x and the
   dispense chip ... use the mini .gt-formula pattern"). Reusable for ANY wide-card
   row that mixes prose tokens with a math cluster. A lone math token is left
   unwrapped so single numerals render exactly as before. */
const isFormulaToken = (tk) => !!tk && (
  tk.n !== undefined || tk.op !== undefined || tk.dispense || tk.clin !== undefined
);
function renderTokenSequence(tokens, vocab = null) {
  const out = [];
  let run = [];
  const flushRun = (key) => {
    if (!run.length) return;
    if (run.length === 1) {
      out.push(renderDecodeToken(run[0], `f-${key}`, vocab));
    } else {
      out.push(
        <span key={`f-${key}`} className="gt-formula">
          {run.map((tk, j) => renderDecodeToken(tk, j, vocab))}
        </span>,
      );
    }
    run = [];
  };
  (tokens || []).forEach((tk, i) => {
    if (isFormulaToken(tk)) {
      run.push(tk);
    } else {
      flushRun(i);
      out.push(renderDecodeToken(tk, `t-${i}`, vocab));
    }
  });
  flushRun('end');
  return out;
}

/* Adapt decode lines to CombatTooltipPanel stat-line objects so the enemy
   tooltip reuses the SAME authored grammar (flat anchors, no nested tooltip,
   no import cycle — CombatTooltip never imports this module).

   `withDefs` (enemy-tooltip surfaces only): a status the enemy INFLICTS carries
   its own minimal def (TAG_META.def, the single source of truth). The enemy
   tooltip can't nest a hover, so the def is surfaced INLINE on a sub-line right
   under the mention rather than hidden behind a second hover (Edward 2026-07-12,
   canon = "Inflicts [chip] / def"). Off by default so the pure 1:1 adapter
   contract (and its canon sweeps) is unchanged for every other caller. */
export function decodeToStatLines(lines, { withDefs = false } = {}) {
  const out = [];
  (lines || [])
    .filter((l) => Array.isArray(l) && l.length)
    .forEach((tokens) => {
      const toks = tokens.filter(Boolean);
      out.push({ stats: toks.map((tk) => tokenToStat(tk)) });
      if (!withDefs) return;
      toks.forEach((tk) => {
        const meta = tk.tag && TAG_META[tk.tag];
        if (meta && meta.def) out.push({ stats: meta.def, sub: true });
      });
    });
  return out;
}

/* Parse authored decode lines into "Inflicts [chip]" ROW descriptors for the
   enemy hover tooltip (Edward's locked 2026-07-12 grammar). Each authored line
   becomes one row:
     - status-inflicting (case 1): { lead, word, meta, aside } — the row renders
       the lead text + the REAL status pill (static) on the left and the status'
       compact flavor aside on the right. The hover keeps the status' canonical
       mechanic definition.
     - bespoke mechanic (case 2, no reusable chip — e.g. lyse's raw max-HP loss):
       { stats } — the whole authored line renders flat, no pill, no def column.
   The enemy-specific magnitude token (`tag(word, v)`) is intentionally dropped:
   the status metadata remains the single source of truth for both surfaces. */
export function decodeToInflicts(lines, vocab = null) {
  return (lines || [])
    .filter((l) => Array.isArray(l) && l.length)
    .map((tokens) => {
      const toks = tokens.filter(Boolean);
      // Converter / multi-chip row: TWO status chips joined by a connector
      // (`Converts [pressure] into [end-organ]`, `Adds [lipid] —Hardens→ [plaque]`).
      // Render the full two-chip transformation instead of collapsing to the first
      // chip (the old bug — eldra's plaque-hardening + Escara's pressure→fibrosis
      // conversion both silently dropped). The connector is whatever the author put
      // BETWEEN the chips — a plain word (`into`) or a causal edge (`Hardens`) — so
      // each enemy keeps its own voice. The DESTINATION chip's def is the single
      // source of the number, matching the single-chip Inflicts rows. Reusable for
      // any enemy whose mechanic is an A→B conversion (Edward 2026-07-14).
      const convertTags = toks.filter((t) => t.tag && (playerStatusMeta(t.tag, vocab) || isEnemyStatus(t.tag, vocab)));
      if (convertTags.length >= 2) {
        const from = convertTags[0];
        const to = convertTags[convertTags.length - 1];
        const fromIdx = toks.indexOf(from);
        const toIdx = toks.indexOf(to);
        const leadTok = toks.slice(0, fromIdx).find((t) => t.t !== undefined || t.j !== undefined);
        const connector = toks.slice(fromIdx + 1, toIdx)
          .filter((t) => t.t !== undefined || t.j !== undefined || t.edge !== undefined);
        return {
          lead: leadTok ? (leadTok.t ?? leadTok.j) : null,
          convert: { from, connector, to },
        };
      }
      const tagTok = toks.find((t) => t.tag && playerStatusMeta(t.tag, vocab));
      if (tagTok) {
        const meta = playerStatusMeta(tagTok.tag, vocab);
        const leadTok = toks.find((t) => t.t !== undefined);
        // Math tokens sitting BETWEEN the lead verb and the chip are a MULTIPLIER on
        // that chip (`Inflicts 2 × [Pressure]`) and must survive into the descriptor.
        // This collapse used to read only the lead and the tag, so authoring num/op
        // here changed nothing on screen — the tokens were dropped in silence. The
        // The hover definition stays the source of the tag's mechanic; this is a
        // count OF the tag, which is a different fact.
        const tagIdx = toks.indexOf(tagTok);
        const mathToks = toks
          .slice(0, tagIdx)
          .filter((t) => t.n !== undefined || t.op !== undefined);
        return {
          lead: leadTok ? leadTok.t : null,
          word: tagTok.tag,
          meta,
          // ⛔ `def` IS PART OF THIS DESCRIPTOR'S CONTRACT AND WAS DROPPED BY ACCIDENT.
          //    396cd651 established it as the source of the tag's OWN number, the comment
          //    six lines up still says so, and natrixPressure asserts it — but the field
          //    stopped being emitted when `aside` was added beside it. Nothing rendered
          //    differently (InflictsRow does not read `def`), which is exactly why it went
          //    unnoticed: a consumer contract can lapse in silence when no pixel depends
          //    on it. Restored verbatim; `aside` keeps its own separate job.
          def: meta.def || null,
          aside: statusAsideLines(meta),
          mathToks: mathToks.length ? mathToks : undefined,
        };
      }
      // Enemy-side status row: `[verb] [enemy chip] | def` — the same grammar,
      // but the chip is the enemy's own live state (glucose/inr/division…) and
      // its def is the status' single-source line (ENEMY_STATUS_META). Gated on
      // explicit registration so only real, tracked enemy state ever becomes a
      // chip (honesty floor).
      const enemyTok = toks.find((t) => t.tag && isEnemyStatus(t.tag, vocab));
      if (enemyTok) {
        const leadTok = toks.find((t) => t.t !== undefined);
        const enemy = resolveVocabEnemyStatus(enemyTok.tag, vocab);
        const defTokens = toks.filter((tk) => tk !== leadTok && tk !== enemyTok);
        return { lead: leadTok ? leadTok.t : null, enemy, defTokens };
      }
      return { stats: toks.map((tk) => tokenToStat(tk, vocab)) };
    })
    .filter((row) => row.meta || row.enemy || row.convert || (row.stats && row.stats.length));
}

/* One status chip for a converter row — resolves a token to the SAME chip chrome the
   Inflicts rows use, for either a player-side status (TAG_META pill) or an enemy-side
   status (EnemyStatusPill). Whether it carries its own hover is the card's render
   context to decide, never this row's. */
function ConvertChip({ tk }) {
  const vocab = React.useContext(StatusVocabularyContext);
  if (tk.tag && isEnemyStatus(tk.tag, vocab)) {
    return <EmbeddedEnemyStatusChip resolved={resolveVocabEnemyStatus(tk.tag, vocab)} />;
  }
  const meta = playerStatusMeta(tk.tag, vocab);
  if (meta) {
    return <EmbeddedStatusChip word={tk.tag} meta={meta} />;
  }
  return <span className="gt-text">{tk.tag}</span>;
}

/* The PERMANENT-INJURY result token. The enemy's serialized `max_hp_delta` is the
   only authority for the magnitude, so the row states the scar the engine actually
   applies (Eldra 5, Escara 1) instead of a prose restatement that has to be kept in
   step by hand. It deliberately mirrors the damage readout's glyph + tabular-numeral
   grammar, but a scarred-heart glyph and the SAME dark maroon the player HP-bar
   sequelae tag uses (PlayerSequelaeTags.jsx) keep permanent injury visually distinct
   from immediate HP damage. */
function SequelaeResult({ amount }) {
  return (
    <span
      className="gt-inflicts__r gt-sequelae-result"
      title={`${amount} HP permanently scarred`}
      aria-label={`${amount} HP permanently scarred`}
    >
      <SequelaeGlyph className="gt-stat__icon gt-sequelae-result__glyph" />
      <span className="gt-sequelae-result__value">{amount}</span>
    </span>
  );
}

/* Reusable Trap result. A disease state owns the status that created the trap, but
   the trapped object is always the same: each later Dispense loses this much damage.
   The visible label names the shared primitive, while the accessible description states
   its fixed Dispense target. Its footprint stays compact and right-aligned like Prophylax. */
function DispenseTrapResult({ amount }) {
  return (
    <span
      className="gt-inflicts__r gt-result-chip gt-dispense-chip gt-dispense-trap-result"
      title={`Each Dispense deals ${amount} less damage for this fight`}
      aria-label={`Each Dispense deals ${amount} less damage for this fight`}
    >
      <span className="gt-result-chip__label gt-dispense-chip__label">Trap</span>
      <span className="gt-dispense-trap-result__value">−{amount}</span>
    </span>
  );
}

function InflictsRow({
  lead, word, meta, aside, stats, enemy, mathToks, defTokens, convert, sequelaeAmount,
  dispenseTrapAmount,
}) {
  const vocab = React.useContext(StatusVocabularyContext);
  // Converter row: [from chip] <connector> [to chip] — a single full-width line that
  // wraps at chip boundaries. NO trailing def: the destination chip carries its own
  // meaning and "permanent" read as clutter here (Edward 2026-07-14). Surfaces
  // Escara's pressure→fibrosis conversion.
  if (convert) {
    return (
      <span className={`gt-inflicts gt-inflicts--convert${sequelaeAmount ? ' has-sequelae-result' : ''}${dispenseTrapAmount ? ' has-dispense-trap-result' : ''}`}>
        <span className="gt-inflicts__l">
          {lead && <span className="gt-text">{lead}</span>}
          <ConvertChip tk={convert.from} />
          {(convert.connector || []).map((tk, i) => renderDecodeToken(tk, `c-${i}`, vocab))}
          <ConvertChip tk={convert.to} />
        </span>
        {sequelaeAmount ? <SequelaeResult amount={sequelaeAmount} /> : null}
        {!sequelaeAmount && dispenseTrapAmount
          ? <DispenseTrapResult amount={dispenseTrapAmount} />
          : null}
      </span>
    );
  }
  // Enemy-side state row: lead verb + the enemy chip (name only) on the left, its
  // single-source def line on the right — the enemy-side mirror of the player
  // "Inflicts [chip] / flavor aside" row.
  if (enemy) {
    return (
      <span className="gt-inflicts">
        <span className="gt-inflicts__l">
          {lead && <span className="gt-text">{lead}</span>}
          <EmbeddedEnemyStatusChip resolved={enemy} />
        </span>
        {defTokens && defTokens.length > 0 ? (
          <span className="gt-inflicts__r">
            {defTokens.map((tk, i) => renderDecodeToken(tk, i, vocab))}
          </span>
        ) : enemy.line ? (
          <span className="gt-inflicts__r"><span className="gt-text">{enemy.line}</span></span>
        ) : null}
      </span>
    );
  }
  return (
    <span className="gt-inflicts">
      <span className="gt-inflicts__l">
        {meta ? (
          <>
            {lead && <span className="gt-text">{lead}</span>}
            {/* A multiplier on the chip goes in the SAME `.gt-formula` readout the
                Deals row uses, so the numeral, the × and the chip inherit that
                block's font, tabular numerals and single uniform 6px flex gap — byte
                parity with the Ten Shin aura row, and no new CSS. Without a
                multiplier the pill renders bare, exactly as before. */}
            {mathToks && mathToks.length ? (
              <span className="gt-formula">
                {mathToks.map((tk, i) => <FormulaToken key={i} tk={tk} />)}
                <EmbeddedStatusChip word={word} meta={meta} />
              </span>
            ) : (
              <EmbeddedStatusChip word={word} meta={meta} />
            )}
          </>
        ) : (
          <StatRow items={stats} />
        )}
      </span>
      {sequelaeAmount
        ? <SequelaeResult amount={sequelaeAmount} />
        : dispenseTrapAmount
          ? <DispenseTrapResult amount={dispenseTrapAmount} />
          : (meta && aside && aside.length > 0 && (
            <span className="gt-inflicts__r"><StatRow items={aside} /></span>
          ))}
    </span>
  );
}

/* The enemy hover tooltip's "Inflicts [chip]" block. Wrapped in the inline
   status-rail scope so the per-status color rules in CombatPlayerbar.css
   (scoped to `.combat-status-rail`) reach the embedded pills. */
export function InflictsRows({ rows }) {
  const valid = (rows || []).filter(Boolean);
  if (!valid.length) return null;
  return (
    <span className="combat-status-rail combat-status-rail--inline gt-inflicts-rows">
      {valid.map((r, i) => <InflictsRow key={i} {...r} />)}
    </span>
  );
}

/* Render one authored formula token: the status pill inline, damage numerals in
   enemy-red, math operators in the muted math tone, a clinical-component label
   (`clin`) dim + small so a bare constant reads as named pathophysiology, everything
   else plain connective text. Used only inside the deals-row formula (not the
   decodeToInflicts collapse). */
function FormulaToken({ tk }) {
  const vocab = React.useContext(StatusVocabularyContext);
  if (tk.tag) {
    const meta = playerStatusMeta(tk.tag, vocab);
    if (meta) return <EmbeddedStatusChip word={tk.tag} meta={meta} />;
    // An enemy-side state inside a damage formula (`harder each [Stage]`) draws its own
    // chip. Gated on an extra vocabulary being supplied, so every card the game fields
    // renders exactly as it did before.
    if (vocab && isEnemyStatus(tk.tag, vocab)) {
      return <EmbeddedEnemyStatusChip resolved={resolveVocabEnemyStatus(tk.tag, vocab)} />;
    }
    return <span className="gt-text">{tk.tag}</span>;
  }
  if (tk.n !== undefined) return <span className="gt-dmg-num">{tk.n}</span>;
  if (tk.clin !== undefined) return <span className="gt-clin">{tk.clin}</span>;
  if (tk.op !== undefined) return <span className="gt-op">{tk.op}</span>;
  if (tk.j) return <span className="gt-join">{renderArrows(tk.j)}</span>;
  return <span className="gt-text">{renderArrows(tk.t)}</span>;
}

/* THE canonical damage READOUT: the sword and the red numeral that every enemy Deals
   row puts in its right column. Exported because it is a TOKEN, not a row: any surface
   that has to state "this deals N" mounts THIS, rather than pairing its own sword with
   its own numeral and drifting. Renders bare (no wrapper element) so it drops straight
   into the caller's existing `.gt-inflicts__r` slot and the markup is byte-identical
   wherever it appears. `amount` may be the em dash the reserved slot uses.

   The colour, weight and tabular numeral live in GameTip.css on `.gt-next-dmg`, whose
   rule names both this file's enemy rows and the drug card's augment rows, so there is
   ONE declaration of what a damage readout looks like. */
export function DamageReadout({ amount }) {
  return (
    <>
      <DamageSword className="gt-stat__icon gt-next-dmg__sword" />
      <span className="gt-next-dmg">{amount}</span>
    </>
  );
}

/* The authored DAMAGE row, in the Inflicts two-column grammar (Edward 2026-07-13):
   the damage FORMULA on the LEFT ("Deals 1 vasoconstriction + 2 x [Pressure]" —
   component-split so the flat direct hit and the pressure-scaled hit are each named
   pathophysiology, math-styled, chip embedded, no trailing "damage" word) and the
   engine's DYNAMIC projected next-attack
   damage on the RIGHT — the actual number THIS enemy will hit you for next attack,
   resolved from the player's live pool load (`next_attack_damage`). The right column
   is ALWAYS RESERVED: in live combat it holds the projected number, and in a static
   context (Compendium page, drug-card warden preview, an announce card before the
   engine has projected) it holds the same sword fronting an em dash. Reserving it is
   what keeps one canonical two-column enemy card instead of two silhouettes, without
   pretending a combat-state projection exists. Rendered as a `.gt-inflicts` so it
   shares the two-column rhythm + hairline divider with the Inflicts rows above it. */
function DealsRow({ tokens, nextDamage, reserveDamageSlot = true }) {
  const toks = (tokens || []).filter(Boolean);
  if (!toks.length) return null;
  // The leading verb ("Deals") renders like the Inflicts lead — normal tooltip text,
  // NOT the math font — and sits at the row's natural gap from the formula (matching
  // "Inflicts" -> pill above). Only the math (numerals / operators / pill) goes in the
  // .gt-formula readout, and the pill keeps its exact Inflicts-row chrome.
  const mathStart = toks.findIndex((tk) => tk.n !== undefined || tk.op !== undefined || tk.clin !== undefined || tk.tag);
  const leadToks = mathStart > 0 ? toks.slice(0, mathStart) : [];
  const mathToks = mathStart >= 0 ? toks.slice(mathStart) : toks;
  const hasResolvedDamage = nextDamage != null && Number.isFinite(Number(nextDamage));
  const showDmg = hasResolvedDamage || reserveDamageSlot;
  return (
    <span className="gt-inflicts gt-inflicts--deals">
      <span className="gt-inflicts__l">
        {leadToks.map((tk, i) => (
          <span key={`lead${i}`} className="gt-text">{renderArrows(tk.t)}</span>
        ))}
        <span className="gt-formula">
          {mathToks.map((tk, i) => <FormulaToken key={i} tk={tk} />)}
        </span>
      </span>
      {showDmg && (
        <span
          className={`gt-inflicts__r${hasResolvedDamage ? '' : ' is-unresolved'}`}
          title={hasResolvedDamage ? undefined : 'Damage resolves from live combat state'}
        >
          <DamageReadout amount={hasResolvedDamage ? Number(nextDamage) : '—'} />
        </span>
      )}
    </span>
  );
}

/* Compose the enemy hover tooltip's BODY from an authored decode entry
   (announceDecode.js) — ONE source so every enemy-tooltip surface (batch roster
   rail, simultaneous entity tiles, solo boss HP bar) renders the SAME structure:
     - Inflicts rows  (decode.announce) "Inflicts [chip] / def"
     - deals row      (formula-selected decode.deals) + dynamic projected damage
   The Inflicts rows AND the deals row live in ONE `.gt-inflicts-rows` container, so
   they read as one two-column stack (mechanic/formula left, resolved value right) with
   the hairline dividers between them. `opts.nextAttackDamage` is the enemy's engine-
   projected next-attack damage, shown on the right of the deals row.
   `opts.attackSpec.damage_formula` selects a `decode.dealsByFormula` override when
   present; otherwise the canonical `decode.deals` row remains unchanged. Un-authored
   enemies fall back to the backend prose blurb. */
/* A persistent enemy AURA row — the passive mechanic that can spread to other
   enemies. Reusable: any decode entry with an `aura: [tokens]` field renders the
   same full token grammar beneath a separator label. */
function AuraRow({ tokens }) {
  const vocab = React.useContext(StatusVocabularyContext);
  const toks = (tokens || []).filter(Boolean);
  if (!toks.length) return null;
  return (
    <span className="gt-aura-block">
      <span className="gt-aura-sep"><span className="gt-aura-sep__label">Aura</span></span>
      <span className="gt-statrow gt-statrow--aura">
        {renderTokenSequence(toks, vocab)}
      </span>
    </span>
  );
}

/* Expand `fromStackDelta` tag tokens into the enemy's real serialized stack count as a
   `N ×` PREFIX — one token becomes three (`num`, `op('×')`, the bare tag) — which is
   the multiplier grammar the Ten Shin aura row and the warden Deals rows already use.
   Above 1 the number is the whole point of the row; at 1 (or when the spec carries no
   stack_delta at all) the token degrades to the plain chip it was before, so no
   existing enemy tooltip changes. Pure — returns new arrays, never mutates the
   authored ENEMY_DECODE entry, which is module-level shared state.

   ⚠️ This expansion is only HALF the work: `decodeToInflicts` must carry the emitted
   num/op tokens through to the row descriptor, or they are silently dropped and the
   screen does not change. See the mathToks branch there. */
export function resolveStackDeltaTokens(lines, stackDelta) {
  if (!Array.isArray(lines)) return lines;
  return lines.map((tokens) => (Array.isArray(tokens)
    ? tokens.flatMap((tk) => {
      if (!tk || !tk.fromStackDelta) return [tk];
      const { fromStackDelta, ...rest } = tk;
      if (!(stackDelta > 1)) return [rest];
      return [{ n: String(stackDelta) }, { op: '×' }, rest];
    })
    : tokens));
}

/* The backend's sequelae ledger records either a fixed HP scar or one applied per
   converted stack. Both are serialized attack facts; status-stack deltas alone are
   transient state and are NOT sequelae. */
export function sequelaeAmountFromAttackSpec(attackSpec) {
  const deltas = [
    attackSpec?.max_hp_delta,
    attackSpec?.max_hp_delta_per_converted_stack,
  ];
  for (const value of deltas) {
    const delta = Math.trunc(Number(value));
    if (Number.isFinite(delta) && delta < 0) return Math.abs(delta);
  }
  return null;
}


/* Every Trap targets Dispense damage. Disease-specific status names remain in the
   authored Inflicts chip; this normalizes the shared per-stack numeric contract for the
   compact interaction chip. Stack count stays on the status application itself. */
export function dispenseTrapAmountFromAttackSpec(attackSpec) {
  const delta = Math.trunc(Number(attackSpec?.dispense_damage_delta));
  if (!Number.isFinite(delta) || delta >= 0) return null;
  return Math.abs(delta);
}

/* ⭐ THE THREE RESULT CONVENTIONS BELOW ARE CANONICAL DEFAULTS, NOT OPT-INS.
   They were first proven on the 8954 admin tooltip workbench, where each surface had
   to ask for them by name. That made the shared primitive render a DIFFERENT enemy
   card depending on which screen called it — the Compendium omitted all three, so its
   pages showed a Deals row with no result column, "−5 max · permanent" prose where the
   scar magnitude belongs, and the long "Each Dispense −1 per stack for this fight"
   sentence where the compact Trap chip belongs. A card whose grammar depends on the
   caller is two cards. The conventions are therefore defaults here, at the one place
   that composes an enemy body, and a caller may still pass `false` to opt OUT (the
   admin parity rails do exactly that when they need the pre-convention silhouette).
   ⛔ Every result is derived from the SERIALIZED attack spec the server shipped for
   this fight; nothing here re-derives a magnitude the backend owns. With no spec the
   derivations return null and the row falls back to the status chip's own def, so an
   un-authored enemy is unchanged. */
export function enemyTooltipSections(decode, fallbackDescription, opts = {}) {
  if (!decode) {
    // An un-authored enemy still gets the canonical enemy chassis: it is the same
    // surface, and a roster that mixes 600px authored cards with 360px prose cards
    // reads as two card families rather than one with a thin entry.
    return fallbackDescription ? [{ enemy: true, lines: [fallbackDescription] }] : [];
  }
  // Resolve any `fromStackDelta` tag against the enemy's SERIALIZED spec before the
  // rows are built — the sibling of the dealsByFormula selection just below. A delta of
  // 1 stays a bare chip: the pill reads as one stack already, and a pill showing "1" is
  // noise.
  // ⛔ PHASE 1 CORRECTED THE REASON THESE EXIST. This used to say they were needed
  // "because one roster entry can carry two variants (attack_spec vs solo_attack_spec)".
  // R26 deleted every solo variant — there is now ONE authored spec per enemy — so that
  // justification is dead. 🔑 Both mechanisms are still RIGHT, for the stronger reason:
  // the tooltip renders whatever spec the SERVER actually shipped for this fight and
  // never re-derives it. That is the law, and it holds no matter how many variants the
  // roster has.
  const stackDelta = Math.trunc(Number(opts.attackSpec?.stack_delta) || 0);
  const statusVocabulary = opts.statusVocabulary || null;
  const announce = resolveStackDeltaTokens(decode.announce, stackDelta);
  const inflicts = decodeToInflicts(announce, statusVocabulary);
  const damageFormula = String(opts.attackSpec?.damage_formula || '');
  const deals = decode.dealsByFormula?.[damageFormula] || decode.deals;
  const hasDeals = Array.isArray(deals) && deals.length > 0;
  const hasAura = Array.isArray(decode.aura) && decode.aura.length > 0;
  // The unified "silent toll" floor (HTN Ten Shin minis, serialized on the enemy's
  // attack_spec): a guaranteed flat strike the enemy ALWAYS lands ON TOP of its formula.
  // Rendered as its OWN Deals row BELOW the mini's full identity row, so the mini keeps
  // everything it had and just gains the floor.
  const silentToll = Math.max(0, Math.trunc(Number(opts.attackSpec?.silent_toll) || 0));
  const hasSilentToll = silentToll > 0;
  if (!inflicts.length && !hasDeals && !hasAura && !hasSilentToll) return [];
  const {
    nextAttackDamage = null,
    reserveDamageSlot = true,
    showSequelaeResult = true,
    showDispenseTrapResult = true,
    // WHERE this body will be mounted. Default = inside a hover popover, so the
    // embedded chips stay decorative. See ENEMY_CARD_RENDER_CONTEXT.
    renderContext = ENEMY_CARD_RENDER_CONTEXT.NESTED_HOVER,
  } = opts;
  const interactiveChips = renderContext === ENEMY_CARD_RENDER_CONTEXT.STANDALONE_CARD;
  const sequelaeAmount = showSequelaeResult
    ? sequelaeAmountFromAttackSpec(opts.attackSpec)
    : null;
  const dispenseTrapAmount = showDispenseTrapResult
    ? dispenseTrapAmountFromAttackSpec(opts.attackSpec)
    : null;
  /* ONE scar token, on the row that CAUSES it, which is the CONVERTER whenever the body
     has one: permanent injury here is what a conversion leaves behind. Escara authors
     its conversion last, so a "final row" rule agreed with it by accident of authoring
     order; Eldra authors a consequence row after its conversion, and the same rule put
     the scar there. The FINAL-row fallback is unchanged for a non-converter. */
  const converterRowIndex = inflicts.findIndex((row) => Boolean(row.convert));
  const sequelaeRowIndex = sequelaeAmount && inflicts.length
    ? (converterRowIndex >= 0 ? converterRowIndex : inflicts.length - 1)
    : -1;
  const dispenseTrapRowIndex = dispenseTrapAmount && inflicts.length
    ? inflicts.length - 1
    : -1;
  // The engine folds the floor into next_attack_damage, so the formula row shows its OWN
  // contribution (total minus the toll) and the toll row shows the flat floor — the two
  // right-hand numbers sum to what the enemy actually deals next.
  const formulaNextDamage = (nextAttackDamage != null && hasSilentToll)
    ? Math.max(0, Number(nextAttackDamage) - silentToll)
    : nextAttackDamage;
  const silentTollRow = [{ t: 'Deals' }, { n: String(silentToll) }, { clin: 'the silent toll' }];
  return [{
    // `enemy` is the ONE marker that says "this body is a true enemy card". It travels
    // with the sections, so CombatTooltipPanel can give every enemy surface the same
    // canonical chassis (GameTip.css `.combat-tt__panel--enemy`) without a single
    // consumer having to opt in, and without a `:has()` rule that a nested enemy mini
    // inside a drug tooltip would trip.
    enemy: true,
    // Rides the SAME channel and for the same reason: the panel has to know its body
    // contains live popovers so it can stop being a scroll box and clipping them
    // (GameTip.css `.combat-tt__panel--interactive-chips`). Derived here, at the one
    // place that composes an enemy body, so no screen can opt in or out by hand.
    interactiveChips,
    content: (
      <EnemyCardRenderContext.Provider value={renderContext}>
        <StatusVocabularyContext.Provider value={statusVocabulary}>
          <span className="gt-enemy-tt">
            {(inflicts.length > 0 || hasDeals || hasAura || hasSilentToll) && (
              <span className="combat-status-rail combat-status-rail--inline gt-inflicts-rows">
                {inflicts.map((r, i) => (
                  <InflictsRow
                    key={i}
                    {...r}
                    sequelaeAmount={i === sequelaeRowIndex ? sequelaeAmount : null}
                    dispenseTrapAmount={i === dispenseTrapRowIndex ? dispenseTrapAmount : null}
                  />
                ))}
                {hasDeals && (
                  <DealsRow
                    key="deals"
                    tokens={deals}
                    nextDamage={formulaNextDamage}
                    reserveDamageSlot={reserveDamageSlot}
                  />
                )}
                {hasSilentToll && (
                  <DealsRow
                    key="silenttoll"
                    tokens={silentTollRow}
                    nextDamage={silentToll}
                    reserveDamageSlot={reserveDamageSlot}
                  />
                )}
                {hasAura && <AuraRow key="aura" tokens={decode.aura} />}
              </span>
            )}
          </span>
        </StatusVocabularyContext.Provider>
      </EnemyCardRenderContext.Provider>
    ),
  }];
}

/* Single-node variant for surfaces that take one tooltip child (the solo boss
   HP bar's tipContent) instead of a sections array. */
export function enemyTooltipContent(decode, opts = {}) {
  const secs = enemyTooltipSections(decode, null, opts);
  return secs.length ? secs[0].content : null;
}

/* Flatten decode lines to plain text (aria-labels, logging). */
export function decodeToText(lines) {
  return (lines || [])
    .filter((l) => Array.isArray(l))
    .map((tokens) => tokens.filter(Boolean).map((tk) => {
      if (tk.tag) {
        // Enemy-status tags flatten to their display name (matching tokenToStat),
        // so the aria/text pass agrees with the flat stat adapter.
        if (!TAG_META[tk.tag] && ENEMY_STATUS_META[tk.tag]) {
          return resolveEnemyStatus({ key: tk.tag }).label;
        }
        return [tk.v, tk.tag].filter((x) => x !== undefined).join(' ');
      }
      if (tk.edge !== undefined) return `${tk.edge} →`;
      if (tk.j) return tk.j;
      if (tk.t !== undefined) return tk.t;
      return [tk.g, tk.v].filter((x) => x !== undefined).join(' ');
    }).join(' '))
    .join('; ');
}
